import { parseAndMigrateStorageEnvelope } from "../data/migrations";
import {
  createStorageEnvelope,
  validateStorageEnvelope,
  type StorageEnvelope,
} from "../data/storage-envelope";
import type { StoredRecordDataMap, StoredRecordType } from "../data/schemas";
import { validationFailure, type ValidationResult } from "../data/validation";

export interface KeyValueStore {
  getItem(key: string): string | null;
  removeItem(key: string): void;
  setItem(key: string, value: string): void;
}

export interface VersionedStorageService {
  load(key: string): ValidationResult<StorageEnvelope>;
  remove(key: string): void;
  save<K extends StoredRecordType>(
    recordType: K,
    key: string,
    data: StoredRecordDataMap[K],
  ): ValidationResult<StorageEnvelope<K>>;
}

export function createVersionedStorageService(
  store: KeyValueStore,
  now: () => string,
): VersionedStorageService {
  return {
    load(key) {
      const source = `storage:${key}`;
      const serialized = store.getItem(key);
      return serialized === null
        ? validationFailure(source, "$", "record not found")
        : parseAndMigrateStorageEnvelope(serialized, source);
    },
    remove(key) {
      store.removeItem(key);
    },
    save(recordType, key, data) {
      const envelope = createStorageEnvelope(recordType, key, now(), data);
      const validation = validateStorageEnvelope(envelope, `storage:${key}`);
      if (!validation.ok) return validation;
      store.setItem(key, JSON.stringify(envelope));
      return { ok: true, value: envelope };
    },
  };
}
